import { jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useEffect } from "react";
import "@popperjs/core";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import stitches from "../theme.config.es.js";
import { useCloseOnBlur } from "./useCloseOnBlur.es.js";
import EzPortal, { PortalContext } from "../EzPortal/EzPortal.es.js";
import usePopper from "../../utils/hooks/usePopper.es.js";
import { clsx } from "../../utils/mergeProps.es.js";
import FocusScope from "../utils/FocusScope/FocusScope.es.js";
const popperStyle = stitches.css({
  zIndex: "$popover-z"
  // this is hard-coded in theme.config.ts for now, but we should really pull it in from mui when we convert popovers to mui
});
const EzPopover = forwardRef((props, ref) => /* @__PURE__ */ jsx(EzPortal, { children: /* @__PURE__ */ jsx(EzPopoverImpl, { ref, ...props }) }));
const EzPopoverImpl = forwardRef(
  ({
    targetRef,
    placement = "bottom",
    matchWidth = false,
    showArrow = false,
    shouldCloseOnBlur = false,
    onClose,
    children,
    classNameSuffix,
    ...rest
  }, ref) => {
    const modifiers = [{ name: "offset", options: { offset: [0, 5] } }];
    if (matchWidth) {
      modifiers.push({
        name: "matchWidth",
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        phase: "beforeWrite",
        requires: ["computeStyles"]
      });
    }
    if (showArrow) modifiers.push({ name: "arrow", enabled: true, options: { padding: 5 } });
    const { popper, reference } = usePopper({ placement, modifiers });
    reference.current = targetRef.current;
    useCloseOnBlur({ shouldCloseOnBlur, onClose, refs: [targetRef, popper] });
    const scopeRef = useRef();
    const mergedRef = useComposedRefs(popper, ref);
    useEffect(() => {
      const triggerEl = targetRef.current;
      const focusScope = scopeRef.current;
      const onKeyDown = (e) => {
        if (e.key !== "Tab" || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        focusScope.focusFirstInScope();
      };
      triggerEl.addEventListener("keydown", onKeyDown, true);
      return () => {
        triggerEl.removeEventListener("keydown", onKeyDown, true);
      };
    }, [targetRef]);
    return /* @__PURE__ */ jsx(
      "div",
      {
        "data-popper-placement": true,
        ref: mergedRef,
        ...rest,
        className: clsx(
          popperStyle(),
          "EzPopover",
          classNameSuffix && `EzPopover-${classNameSuffix}`
        ),
        children: /* @__PURE__ */ jsx(PortalContext.Provider, { value: popper, children: /* @__PURE__ */ jsx(FocusScope, { restoreFocus: true, ref: scopeRef, children }) })
      }
    );
  }
);
EzPopover.displayName = "EzPopover";
EzPopoverImpl.displayName = "EzPopoverImpl";
export {
  EzPopover as default
};
//# sourceMappingURL=EzPopover.es.js.map
